import { useMutation } from '@tanstack/react-query';
import { ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router';

import { Button, Input } from '@axioma/ui';

import { api, extractErrorMessage } from '../lib/api.js';
import { useAuthStore } from '../stores/auth.js';

export function TwoFactorVerify(): JSX.Element {
  const pendingToken = useAuthStore((s) => s.pendingToken);
  const setToken = useAuthStore((s) => s.setToken);
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();

  const [code, setCode] = useState('');
  const [usingRecovery, setUsingRecovery] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const verify = useMutation({
    mutationFn: async (rawCode: string) => {
      const endpoint = usingRecovery ? '/auth/2fa/recover' : '/auth/2fa/verify';
      const { data } = await api.post<{ token: string }>(endpoint, { code: rawCode });
      return data;
    },
    onSuccess: (data) => {
      setToken(data.token);
      navigate('/', { replace: true });
    },
    onError: (err) => setError(extractErrorMessage(err)),
  });

  if (!pendingToken) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="ax-hero-bg min-h-screen w-screen flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-ax-night border border-ax-line mb-4">
            <ShieldCheck size={20} className="text-ax-aurora" />
          </div>
          <h1 className="font-display text-3xl text-ax-text-primary tracking-tight mb-1">
            Verificação 2FA
          </h1>
          <p className="ax-body text-ax-text-secondary">
            Olá, {user?.name}. Insira o código do seu app autenticador.
          </p>
        </div>

        <div className="bg-ax-graphite border border-ax-line rounded-ax-lg p-8">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setError(null);
              verify.mutate(code);
            }}
            className="space-y-5"
          >
            <Input
              label={usingRecovery ? 'Código de recuperação' : 'Código TOTP (6 dígitos)'}
              inputMode={usingRecovery ? 'text' : 'numeric'}
              maxLength={usingRecovery ? 14 : 6}
              placeholder={usingRecovery ? 'XXXX-XXXX-XXXX' : '000000'}
              autoComplete="one-time-code"
              mono
              autoFocus
              value={code}
              onChange={(e) => setCode(usingRecovery ? e.target.value.toUpperCase() : e.target.value.replace(/\D/g, ''))}
              error={error ?? undefined}
              required
            />
            <Button
              type="submit"
              variant="signature"
              size="lg"
              fullWidth
              loading={verify.isPending}
              disabled={usingRecovery ? code.length < 12 : code.length !== 6}
            >
              Verificar e entrar
            </Button>
          </form>

          <button
            type="button"
            onClick={() => {
              setUsingRecovery((v) => !v);
              setCode('');
              setError(null);
            }}
            className="mt-5 w-full text-center font-mono text-[11px] text-ax-text-tertiary uppercase tracking-wider hover:text-ax-aurora transition-colors"
          >
            {usingRecovery ? 'Voltar e usar TOTP' : 'Perdeu o segundo fator? Usar código de recuperação'}
          </button>
        </div>
      </div>
    </div>
  );
}
